import { BAR_SLOTS, CombatEventKind, EffectLimit, HitFlag, MAX_ACTS_PER_SECOND, NO_HANDLE, RefusalReason, abilityByNumber, abilityNumber, aimOfDirection, aimOfFacing, aimToward, barOf, blowOf, clearlyShort, damageTypeOfFlags, effectByNumber, facingOfAim, isEffectEvent, limitsOfEffect, negationOfFlags, stepOfUsedFlags, unpaidOfUsedFlags, type Ability, type CombatRecord, type Direction, type EffectsRecord, type Facing, type ResourceId, type Rules, type VitalsRecord, moveSpeedOfEffects } from "odyssey-shared";
import type { FightSounds } from "../audio/cues.ts";
import type { HitFeed } from "./hit-feed.ts";
import type { FrozenGate } from "../moderation/frozen-gate.ts";
import { KillerNames } from "./killer-names.ts";
import { BUFFER_TICKS, Readiness } from "./readiness.ts";
import { clickSwings, type StanceStore } from "./stance.ts";
import { SwingPlan } from "./swing-plan.ts";
import { deathLine, refusalLine, weakLine } from "./wording.ts";

/**
 * The combat event kinds this page reads; an event of any other kind is skipped with one warning for its kind.
 */
const KNOWN_EVENT_KINDS: ReadonlySet<number> = new Set<number>(Object.values(CombatEventKind));

/**
 * The fight on the page (P2.1-T4 and P2.1-T4b; ADR-073 decision 15 and its amendment, ADR-074 decisions 2 to 5;
 * CLIENT.md sections 1, 2 and 5): a left click swings the held weapon's basic blow toward the pointer while the weapon is
 * drawn (the stance, `stance.ts`, P2.2-T13: sheathed, a click only selects, and a press on a slot draws), starting its
 * strip and the turn to the aim's facing the moment the act is sent; the combat frame's `used` confirms the swing and
 * gives the blow's ready tick, `refused` cancels the swing it answers back to idle with its reason on the message
 * line, a hit the player takes plays its hurt, every hit it takes or deals queues a number, and its own death names
 * who killed it and with what. `vitals` lay the character down and stand it up. The page withholds an act it knows
 * would be refused for cooldown (`readiness.ts`): a click in the blow's last `BUFFER_TICKS` before it is ready is
 * held and fires on the input that makes it ready, and an earlier one is ignored. The owner's `effects` frame names the
 * effects the character bears (ADR-079 decision 4): the union of what they forbid, read from the page's own rules, goes
 * to the body's prediction, and while acting is forbidden a click swings nothing and says why, and a held click is
 * dropped. The page decides nothing here: no hit, no health, no effect, and the server judges every act it sends.
 *
 * The ability bar (P2.2-T4, question 4) uses the same path: its slots hold the abilities the player's bar names
 * (`setBar`, P2.8-T29; the default bar's are `barOf`'s in order), and the left click on the world is the basic blow
 * wherever it stands on the bar, or off it (ADR-121 decision 1.5). Readiness is kept by ability, so a key, a click on a
 * slot and a left click that use one ability share one readiness, one held press and one windup. A key aims at the pointer as a click does; a click on a slot aims at the
 * selected target, or along the facing with none. A displacement (Evasion) is aimed by none of these: by key, slot
 * click or held press it goes the way the movement intent holds, 8-way, or along the facing when none is held, and the
 * cursor never steers it. A slot whose cost the last `vitals` show the player clearly cannot pay,
 * short even after the regeneration the window may bring (`clearlyShort`, ADR-073's amendment of 2026-10-02, item 6), sends nothing
 * and says so, as the server's refusal would; nearer the threshold the act is sent and the server decides. The fight tells the bar each `used` and `refused` of its own.
 *
 * The held weapon's basic blow is never withheld for cost (ADR-117 decision 9): the map server swings it unpaid and
 * weaker when its cost cannot be paid, and says so in its `used` event's `Unpaid` bit, which is the only thing the page
 * reads it from. The first unpaid `used` of the basic blow after a paid one, or the first of a session, says one line;
 * the swings after it say nothing until a paid one comes between, since the blow swings twice a second.
 *
 * A swing's strip is the server's to choose (ADR-073's amendment "the Strike combo and the Heavy Strike chain", item 8):
 * the page starts the combo step it predicts (`swing-plan.ts`) and swaps to the step `used` carries when they differ; a
 * chain's later strips play from the press on the page's schedule and its root is predicted over the inputs after the
 * act, both ended by a refusal, a stun or a death, and every press waits while the chain's root runs.
 */

/**
 * The limits a frame of the character's effects sets: the union of what each effect forbids, read from the page's
 * rules; a number the rules do not hold forbids nothing.
 *
 * @param rules The page's rules.
 * @param effects The owner's `effects` frame, whose first `count` entries are read.
 * @returns The `EffectLimit` bits, 0 for none.
 */
export function limitsOfFrame(rules: Rules, effects: Readonly<EffectsRecord>): number {
  let limits = 0;
  for (let index = 0; index < effects.count; index += 1) {
    const entry = effects.effects[index];
    const effect = entry === undefined ? undefined : effectByNumber(rules, entry.effect);
    if (effect !== undefined) {
      limits |= limitsOfEffect(effect);
    }
  }
  return limits;
}

/**
 * The window the page counts its own acts over, in ms: the map server admits `MAX_ACTS_PER_SECOND` a second on a
 * window that starts at the first act after the last ended and drops the rest unanswered (ADR-074 decision 2), so the
 * page keeps ten to a longer span, and arrival jitter never pushes an eleventh into one server second.
 */
export const ACT_WINDOW_MS = 1200;

/**
 * What the fight moves on the page's own character (`local-player.ts`).
 */
export interface FightBody {
  /** Whether the character is dead, as its `vitals` last said. */
  readonly dead: boolean;
  /** The seq of the last input sent, which an act sent now would follow. */
  readonly inputSeq: number;
  /**
   * Starts a swing on intent: plays its strip once and predicts the turn.
   *
   * @param ability The ability's number, which a refusal names.
   * @param strip The ability's strip.
   * @param facing The facing the aim turns the character to.
   * @param nowMs Scene time in ms.
   * @param rootInputs The inputs after the act its chain roots, 0 for an act with no chain.
   * @returns The seq of the last input sent, which the act names as its `afterSeq`.
   */
  swing(ability: number, strip: string, facing: Facing, nowMs: number, rootInputs: number): number;
  /**
   * Schedules a chain's later strips from the press.
   *
   * @param strips The later blows' strips, in order.
   * @param offsetsMs Each one's time after the press, in ms.
   * @param nowMs The press's scene time in ms.
   */
  follow(strips: readonly string[], offsetsMs: readonly number[], nowMs: number): void;
  /**
   * Swaps the strip playing for the one the server chose, from the same start.
   *
   * @param from The strip the page started.
   * @param to The strip the server chose.
   */
  swapStrip(from: string, to: string): void;
  /**
   * Ends the page's chain: its later strips not yet started are dropped and its root ends at the last input made.
   */
  interrupt(): void;
  /**
   * Cancels a refused ability's swing started on intent: its predicted turn and hold, and its strip back to idle if it
   * is the one playing.
   *
   * @param ability The refused ability's number.
   * @param strip The refused ability's strip.
   */
  cancelSwing(ability: number, strip: string): void;
  /**
   * Plays the hurt strip for a hit the player took.
   *
   * @param nowMs Scene time in ms.
   */
  hurt(nowMs: number): void;
  /**
   * Lays the character down or stands it up.
   *
   * @param dead Whether it is dead.
   * @param nowMs Scene time in ms.
   */
  setDead(dead: boolean, nowMs: number): void;
  /**
   * Where a swing starts, in the pointer's world units: the centre of the feet box (ADR-073 decision 7).
   *
   * @param out Where the point is written.
   * @returns False when no character is drawn.
   */
  origin(out: { x: number; y: number }): boolean;
  /**
   * The character's predicted facing now.
   *
   * @returns The facing.
   */
  facing(): Facing;
  /**
   * Hands the movement limits the server confirmed to the character's prediction, which steps and replays every input
   * under them until they change (ADR-079 decision 4).
   *
   * @param limits The `EffectLimit` bits in force, 0 for none.
   */
  limit(limits: number): void;
  /**
   * Hands the move speed the server confirmed to the character's prediction, the sum of its effects' `moveSpeed`
   * (ADR-144's amendment of 2026-11-01).
   *
   * @param percent The percent, 0 for none.
   */
  slow(percent: number): void;
}

/**
 * What the fight asks of the session and the scene.
 */
export interface FightHost {
  readonly body: FightBody;
  /** Shows a sentence on the message line. */
  readonly say: (text: string) => void;
  /**
   * Told of the page's own death in its two wordings, which the message line never shows: the chat writes the first
   * as a death line and the death screen shows the second as its cause (ADR-073's amendment "the respawn is the
   * player's, and a death is said in chat"; ADR-068's amendment "chat lines show the first name", item 5).
   *
   * @param line The death line (`deathLine`) naming the killer as a chat line does (`chatNameOf`).
   * @param cause The same sentence naming the killer in full (`nameOf`).
   */
  readonly onDeath?: (line: string, cause: string) => void;
  /** Where hits wait for their numbers. */
  readonly hits: HitFeed;
  /** The name the page holds for a handle, a character's in full, or null. */
  readonly nameOf: (handle: number) => string | null;
  /** The name a chat line shows for a handle, a character's first name, or null; without it a chat line names as `nameOf` does. */
  readonly chatNameOf?: (handle: number) => string | null;
  /** Whether the connection can send now. */
  readonly ready: () => boolean;
  /** The selected target, `NO_HANDLE` for none, which every act names (P2.2-T3). */
  readonly target: () => number;
  /**
   * The movement intent held now, from the keys (or any later movement input that feeds the same intent), which aims a
   * displacement: the way the character is moving.
   */
  readonly heading: () => Direction;
  /** Sends an `act`. */
  readonly send: (afterSeq: number, ability: number, target: number, aim: number) => void;
  /**
   * Where the selected target's feet are drawn, in the body's origin's units, which a click on a slot aims at.
   *
   * @param out Where the point is written.
   * @returns False when none is selected or drawn.
   */
  readonly targetPoint?: (out: { x: number; y: number }) => boolean;
  /**
   * Told of each `used` answering the page's own act (the bar's cooldown).
   *
   * @param ability The ability's number.
   * @param cooldownTicks The event's ticks until ready.
   * @param nowMs Scene time in ms.
   */
  readonly onUsed?: (ability: number, cooldownTicks: number, nowMs: number) => void;
  /**
   * Told of each `cooldown` for the page's own character, a cooldown running as it joined (the bar's cooldown, with no
   * sound; ADR-084's note for P2.3-T8).
   *
   * @param ability The ability's number.
   * @param cooldownTicks The event's ticks until ready.
   * @param nowMs Scene time in ms.
   */
  readonly onCooldown?: (ability: number, cooldownTicks: number, nowMs: number) => void;
  /**
   * Told of each `refused` answering the page's own act (the cast bar's reason).
   *
   * @param ability The ability's number.
   * @param reason The reason code.
   * @param cancelled Whether it cancelled the windup playing.
   * @param nowMs Scene time in ms.
   */
  readonly onRefused?: (ability: number, reason: number, cancelled: boolean, nowMs: number) => void;
  /**
   * The sounds of the page's own acts, the hits it receives, its standing up, and refusals (ADR-085).
   */
  readonly sound?: FightSounds;
  /**
   * The page's stance (`stance.ts`, P2.2-T13): sheathed, a left click swings nothing; a press on a slot holding an
   * ability, or an ability's hit landing on the character, draws it. Without one the weapon is always drawn.
   */
  readonly stance?: Pick<StanceStore, "getSnapshot" | "apply">;
  /**
   * The page's refusal of an act while a Game Master has frozen its player (`moderation/frozen-gate.ts`, ADR-056's
   * amendment of 2026-10-06, decision 42): a press it refuses starts no swing, strip, sound, stance change or
   * prediction and sends nothing, and the gate says why. Without one no press is refused for a freeze.
   */
  readonly gate?: Pick<FrozenGate, "frozen" | "refuses">;
}

/**
 * Where a held press aims when it fires: at the point the frame hands `update` (a click or a key), or at the selected
 * target (a click on a slot).
 */
const HeldAim = { Point: 0, Target: 1 } as const;
type HeldAim = (typeof HeldAim)[keyof typeof HeldAim];

/**
 * No press held.
 */
const NOT_HELD = -1;

/**
 * The number no ability has, which stands for the basic blow of a weapon that names none.
 */
const NO_ABILITY = 0;

/**
 * The place past the bar's ten that always holds the held weapon's basic blow: what a left click on the world uses,
 * wherever the blow stands on the bar, or off it.
 */
const BASIC_SLOT = BAR_SLOTS;

/**
 * The resources before the first `vitals`: unknown, so no cost is withheld until the server has said.
 */
const UNKNOWN_POOLS: Readonly<PoolsAndMaximums> = { health: Number.POSITIVE_INFINITY, stamina: Number.POSITIVE_INFINITY, focus: Number.POSITIVE_INFINITY, maxHealth: 0, maxStamina: 0, maxFocus: 0 };

/**
 * Each resource and its maximum, as a `vitals` frame carries them.
 */
export type PoolsAndMaximums = Pick<VitalsRecord, "health" | "stamina" | "focus" | "maxHealth" | "maxStamina" | "maxFocus">;

/**
 * The fight.
 */
export class Fight {
  private readonly bar: (Ability | undefined)[];
  private readonly numbers: number[];
  private readonly basic: number;
  private readonly basicResource: ResourceId | null;
  private weak = false;
  private readonly sentAt: number[];
  private sentNext = 0;
  private readonly point = { x: 0, y: 0 };
  private readonly aimPoint = { x: 0, y: 0 };
  private deadYet = false;
  private readonly readiness: Readiness;
  private readonly unknownKinds = new Uint8Array(256);
  private readonly killers = new KillerNames();
  private readonly chatKillers = new KillerNames();
  private held = NOT_HELD;
  private heldAim: HeldAim = HeldAim.Point;
  private limits = 0;
  private readonly pools: Record<ResourceId, number> = { health: UNKNOWN_POOLS.health, stamina: UNKNOWN_POOLS.stamina, focus: UNKNOWN_POOLS.focus };
  private readonly maximums: Record<ResourceId, number> = { health: 0, stamina: 0, focus: 0 };
  private readonly plan: SwingPlan;
  private readonly started: string[];

  /**
   * @param host The body, the message line, the hit feed, names and the connection.
   * @param rules The page's rules, which name the bar's abilities, every ability's name, its cooldown and its costs.
   */
  public constructor(
    private readonly host: FightHost,
    private readonly rules: Rules,
  ) {
    const basic = blowOf(rules, rules.startingWeapon, "basic");
    this.basic = basic === null ? NO_ABILITY : abilityNumber(rules, basic.id);
    this.bar = new Array<Ability | undefined>(BASIC_SLOT + 1).fill(undefined);
    this.numbers = new Array<number>(BASIC_SLOT + 1).fill(NO_ABILITY);
    this.bar[BASIC_SLOT] = basic ?? undefined;
    this.numbers[BASIC_SLOT] = this.basic;
    this.setBar(barOf(rules).map((ability) => abilityNumber(rules, ability.id)));
    this.basicResource = basic?.costs[0]?.resource ?? null;
    this.sentAt = new Array<number>(MAX_ACTS_PER_SECOND).fill(Number.NEGATIVE_INFINITY);
    this.readiness = new Readiness(rules.abilities.size);
    this.plan = new SwingPlan(rules);
    this.started = rules.tables.abilities.map((ability) => ability.strip);
    this.started.unshift("");
  }

  /**
   * Names the ability each slot of the bar holds (P2.8-T29): the player's bar or the default bar, as the bar resolves
   * it. A slot holding an item, or nothing, holds no ability here; its press goes to the bar's store. A press held for a
   * slot is dropped, so it never fires whatever a change put there.
   *
   * @param numbers Each slot's ability number, slot 1 first, 0 for none; past `BAR_SLOTS` ignored.
   */
  public setBar(numbers: readonly number[]): void {
    for (let slot = 0; slot < BAR_SLOTS; slot += 1) {
      const number = numbers[slot] ?? NO_ABILITY;
      this.bar[slot] = number === NO_ABILITY ? undefined : abilityByNumber(this.rules, number);
      this.numbers[slot] = this.bar[slot] === undefined ? NO_ABILITY : number;
    }
    if (this.held !== NOT_HELD && this.held !== BASIC_SLOT) {
      this.held = NOT_HELD;
    }
  }

  /**
   * A left click on the world: uses the held weapon's basic blow, wherever it stands on the bar (ADR-121 decision 1.5), toward the point clicked
   * (`use`), while the weapon is drawn; sheathed, it starts, sends and says nothing (P2.2-T13), and the session's
   * selection of what stands there is all the click does.
   *
   * @param x The point clicked, in the same world units as the body's origin.
   * @param y The point clicked, down positive.
   * @param nowMs Scene time in ms.
   * @returns True when an act was sent and the swing started.
   */
  public swing(x: number, y: number, nowMs: number): boolean {
    const stance = this.host.stance;
    if (stance !== undefined && !clickSwings(stance.getSnapshot())) {
      return false;
    }
    return this.use(BASIC_SLOT, x, y, nowMs);
  }

  /**
   * The touch Attack button: the weapon's basic blow aimed at the selected target's feet, or along the facing with none,
   * while the weapon is drawn; sheathed it does what a click does and swings nothing.
   *
   * @param nowMs Scene time in ms.
   * @returns True when an act was sent and the swing started.
   */
  public attack(nowMs: number): boolean {
    const stance = this.host.stance;
    if (stance !== undefined && !clickSwings(stance.getSnapshot())) {
      return false;
    }
    return this.useAtTarget(BASIC_SLOT, nowMs);
  }

  /**
   * Uses a slot toward a point, as a left click on the world and a slot's key do, once its ability is ready, unless the
   * slot is empty, the character is dead, the page has already sent its acts for the window, or the connection cannot
   * send. The strip and the turn start at once; the act names the page's selection (P2.2-T3), which a cone does not
   * read. A press at most `BUFFER_TICKS` before the ability is ready is held for `update` to fire, in place of any held
   * before; an earlier one starts nothing and says nothing. While the character's effects forbid acting, or the last
   * `vitals` show it cannot pay for an ability other than the weapon's basic blow, a press starts and sends nothing and
   * the message line says so, as the server's refusal would.
   *
   * @param slot The slot's index, from 0.
   * @param x The point aimed at, in the same world units as the body's origin.
   * @param y The point's y, down positive.
   * @param nowMs Scene time in ms.
   * @returns True when an act was sent and the swing started.
   */
  public use(slot: number, x: number, y: number, nowMs: number): boolean {
    if (!this.admits(slot, HeldAim.Point)) {
      return false;
    }
    this.aimPoint.x = x;
    this.aimPoint.y = y;
    return this.fire(slot, this.aimPoint, nowMs);
  }

  /**
   * A click on a slot: uses it as `use` does, aimed at the selected target's feet, or along the character's facing when
   * no target is drawn.
   *
   * @param slot The slot's index, from 0.
   * @param nowMs Scene time in ms.
   * @returns True when an act was sent and the swing started.
   */
  public useAtTarget(slot: number, nowMs: number): boolean {
    if (!this.admits(slot, HeldAim.Target)) {
      return false;
    }
    return this.fire(slot, this.targetAim(), nowMs);
  }

  /**
   * Fires the held press the moment its ability is ready: called each frame right after the frame's inputs are sent, so
   * the act rides with the input it follows. A held click or key aims at the point handed now, a held slot click at the
   * selected target. A held press is dropped when the character is dead, its effects forbid acting, the connection
   * cannot send, or a Game Master has frozen the player (`FightHost.gate`), which says nothing here.
   *
   * @param x The world point under the pointer now, which a held click or key aims at.
   * @param y The point's y, down positive.
   * @param nowMs Scene time in ms.
   * @returns True when the held press fired.
   */
  public update(x: number, y: number, nowMs: number): boolean {
    const slot = this.held;
    if (slot === NOT_HELD) {
      return false;
    }
    const body = this.host.body;
    if (body.dead || (this.limits & EffectLimit.Act) !== 0 || !this.host.ready() || this.host.gate?.frozen() === true) {
      this.held = NOT_HELD;
      return false;
    }
    if (this.readiness.ticksUntilReady(this.numbers[slot] as number, body.inputSeq) > 0) {
      return false;
    }
    if (this.heldAim === HeldAim.Target) {
      return this.fire(slot, this.targetAim(), nowMs);
    }
    this.aimPoint.x = x;
    this.aimPoint.y = y;
    return this.fire(slot, this.aimPoint, nowMs);
  }

  /**
   * Whether a press on a slot may fire now: the slot holds an ability, the character lives and no Game Master has
   * frozen the player (a frozen press is refused by `FightHost.gate` before anything starts, the weapon's draw
   * included, and drops a held press), which draws the weapon
   * whether or not the press then fires (P2.2-T13), so a press withheld for its cooldown still leaves the next click a
   * swing; the connection can send,
   * its effects allow acting and it is not clearly short of its cost, each refusal the page foresees said on the message line; and its ability
   * is ready, a press in the last `BUFFER_TICKS` held instead. The weapon's basic blow is never checked against its
   * cost (ADR-117 decision 9).
   *
   * @param slot The slot's index, from 0.
   * @param aim Where a held press aims.
   * @returns True when it may fire now.
   */
  private admits(slot: number, aim: HeldAim): boolean {
    const ability = this.bar[slot];
    const body = this.host.body;
    if (ability === undefined || body.dead) {
      return false;
    }
    if (this.host.gate?.refuses() === true) {
      this.held = NOT_HELD;
      return false;
    }
    this.host.stance?.apply("ability");
    if (!this.host.ready()) {
      return false;
    }
    if ((this.limits & EffectLimit.Act) !== 0) {
      this.host.say(refusalLine(RefusalReason.Stunned, ability.name));
      this.host.sound?.refused();
      return false;
    }
    if (this.numbers[slot] !== this.basic && clearlyShort(this.rules, this.pools, this.maximums, ability.costs)) {
      this.host.say(refusalLine(RefusalReason.Cost, ability.name));
      this.host.sound?.refused();
      return false;
    }
    const wait = this.readiness.ticksUntilReady(this.numbers[slot] as number, body.inputSeq);
    if (wait > 0) {
      if (wait <= BUFFER_TICKS) {
        this.held = slot;
        this.heldAim = aim;
      }
      return false;
    }
    return true;
  }

  /**
   * The point a click on a slot aims at: the selected target's feet, or null to aim along the facing.
   *
   * @returns The point, or null.
   */
  private targetAim(): { x: number; y: number } | null {
    return this.host.targetPoint?.(this.aimPoint) === true ? this.aimPoint : null;
  }

  /**
   * Reads an own snapshot's acknowledgement into the tick estimate.
   *
   * @param tick The snapshot's tick.
   * @param ackSeq The last input seq the server had applied by it.
   */
  public acknowledge(tick: number, ackSeq: number): void {
    this.readiness.acknowledge(tick, ackSeq);
  }

  /**
   * Starts a slot's strip and turn toward a point, or along the facing, and sends its act, unless no character is drawn
   * or the window of acts is full; either leaves a held press held. A displacement ignores the point and goes the way
   * the movement intent holds, or along the facing with none (ADR-073's amendment "the way a displacement goes, and a
   * swing keeps its facing"), however it was pressed.
   *
   * @param slot The slot's index, from 0, holding an ability.
   * @param at The point, in the body's origin's units, or null to aim along the facing; a displacement reads neither.
   * @param nowMs Scene time in ms.
   * @returns True when the act was sent.
   */
  private fire(slot: number, at: { readonly x: number; readonly y: number } | null, nowMs: number): boolean {
    const body = this.host.body;
    const ability = this.bar[slot] as Ability;
    const number = this.numbers[slot] as number;
    if (!body.origin(this.point) || nowMs - (this.sentAt[this.sentNext] as number) < ACT_WINDOW_MS) {
      return false;
    }
    const aim = (ability.displacement !== null ? aimOfDirection(this.host.heading()) : at === null ? null : aimToward(at.x - this.point.x, at.y - this.point.y)) ?? aimOfFacing(body.facing());
    const chain = this.plan.chainOf(number);
    const root = chain?.rootInputs ?? 0;
    const strip = this.plan.press(number, ability, this.readiness.readyTick(number), this.readiness.tickOf(body.inputSeq));
    this.started[number] = strip;
    const afterSeq = body.swing(number, strip, facingOfAim(aim), nowMs, root);
    if (chain !== null) {
      body.follow(chain.strips, chain.offsetsMs, nowMs);
    }
    this.host.send(afterSeq, number, this.host.target(), aim);
    this.host.sound?.used(number);
    this.readiness.sent(number, afterSeq, ability.cooldownTicks, root);
    this.held = NOT_HELD;
    this.sentAt[this.sentNext] = nowMs;
    this.sentNext = (this.sentNext + 1) % this.sentAt.length;
    return true;
  }

  /**
   * Reads a combat frame's events for the page's own character: `used` gives its ability's ready tick, and the basic
   * blow's says the weak line when it is the first to carry `Unpaid` since one that did not (ADR-117 decision 9), `cooldown`
   * gives a ready tick told at the join and tells the bar, with no swing and no sound (ADR-084's note for P2.3-T8), `refused`
   * says its reason and cancels the swing it answers when that is the newest act awaited, each told to the bar, a hit it takes plays its
   * hurt unless it was negated or a periodic effect's tick, a hit it takes draws its weapon unless it was a periodic
   * effect's tick (ADR-073's note "being attacked draws the weapon"), a hit it takes or deals queues a number, or its cause's word
   * when the hit was negated (ADR-079 decision 6), and its death names its killer and the ability, or for a tick the
   * effect's cause ("Poison", its amendment of 2026-10-02), to `onDeath`, never on the message line, since the death
   * screen and the chat say it. The killer is named as the view holds it, or else by the name the view held when its
   * last hit on the player arrived (`KillerNames`, its amendment for P2.5-T2), for a poisoner who has left the view,
   * in two wordings: by the name a chat line shows for the chat, and in full for the death screen. A `heal` the
   * page's character receives or gives queues its number as a heal, which the numbers draw green with a plus sign
   * (ADR-091 decision 11). Events about others are skipped. An event of a kind the page does not know is skipped and
   * the events after it are read, with one warning in the console for each such kind, however many arrive
   * (PROTOCOL.md 5.7).
   *
   * @param record The frame, whose first `count` events are this tick's.
   * @param own The page's own handle.
   * @param nowMs Scene time in ms.
   */
  public receive(record: CombatRecord, own: number, nowMs: number): void {
    const host = this.host;
    for (let index = 0; index < record.count; index += 1) {
      const event = record.events[index];
      if (event === undefined) {
        break;
      }
      if (event.kind === CombatEventKind.Used && event.source === own) {
        this.readiness.used(event.ability, record.tick, event.amount, this.plan.chainOf(event.ability)?.rootInputs ?? 0);
        const swap = this.plan.confirm(event.ability, stepOfUsedFlags(event.flags));
        if (swap !== null) {
          host.body.swapStrip(swap.from, swap.to);
        }
        host.onUsed?.(event.ability, event.amount, nowMs);
        if (event.ability === this.basic) {
          const unpaid = unpaidOfUsedFlags(event.flags);
          if (unpaid && !this.weak) {
            host.say(weakLine(this.basicResource, this.nameOfAbility(event.ability)));
          }
          this.weak = unpaid;
        }
      } else if (event.kind === CombatEventKind.Cooldown && event.source === own) {
        this.readiness.told(event.ability, record.tick, event.amount);
        host.onCooldown?.(event.ability, event.amount, nowMs);
      } else if (event.kind === CombatEventKind.Refused && event.source === own) {
        const cancelled = this.readiness.refused(event.ability, this.plan.chainOf(event.ability)?.rootInputs ?? 0);
        if (cancelled) {
          this.plan.refused(event.ability);
          host.body.cancelSwing(event.ability, this.started[event.ability] ?? abilityByNumber(this.rules, event.ability)?.strip ?? "");
        }
        host.say(refusalLine(event.amount, this.nameOfAbility(event.ability)));
        host.sound?.refused();
        host.onRefused?.(event.ability, event.amount, cancelled, nowMs);
      } else if (event.kind === CombatEventKind.Hit && (event.target === own || event.source === own)) {
        const taken = event.target === own;
        if (taken && event.source !== NO_HANDLE) {
          this.killers.hitBy(event.source, host.nameOf(event.source));
          this.chatKillers.hitBy(event.source, host.chatNameOf?.(event.source) ?? null);
        }
        if (taken && !isEffectEvent(event.flags)) {
          host.stance?.apply("attacked");
        }
        if (taken && event.amount > 0 && !isEffectEvent(event.flags)) {
          host.body.hurt(nowMs);
        }
        host.hits.push(event.target, event.amount, (event.flags & HitFlag.Critical) !== 0, taken, damageTypeOfFlags(event.flags), negationOfFlags(event.flags), false);
        if (!isEffectEvent(event.flags)) {
          host.sound?.hit(event.ability, event.flags, event.amount, event.source, taken);
        }
      } else if (event.kind === CombatEventKind.Death && event.target === own) {
        const killer = event.source === NO_HANDLE ? null : (host.nameOf(event.source) ?? this.killers.nameOf(event.source));
        const chatKiller = event.source === NO_HANDLE ? null : (host.chatNameOf?.(event.source) ?? this.chatKillers.nameOf(event.source) ?? killer);
        const ability = isEffectEvent(event.flags) ? this.causeOfEffect(event.ability) : this.nameOfAbility(event.ability);
        host.onDeath?.(deathLine(chatKiller, ability), deathLine(killer, ability));
      } else if (event.kind === CombatEventKind.Heal) {
        if (event.target === own || event.source === own) {
          host.hits.push(event.target, event.amount, false, event.target === own, 0, negationOfFlags(0), true);
        }
      } else if (!KNOWN_EVENT_KINDS.has(event.kind) && this.unknownKinds[event.kind & 0xff] === 0) {
        this.unknownKinds[event.kind & 0xff] = 1;
        console.warn(`skipping combat events of kind ${event.kind}, which this page does not know`);
      }
    }
  }

  /**
   * Reads the owner's `effects` frame (ADR-079 decisions 4 and 9): the limits its effects set go to the body's
   * prediction, and a held press is dropped and the page's chain ended once acting is forbidden.
   *
   * @param effects The frame, whose first `count` entries are the character's effects now.
   */
  public effects(effects: Readonly<EffectsRecord>): void {
    this.limits = limitsOfFrame(this.rules, effects);
    if ((this.limits & EffectLimit.Act) !== 0) {
      this.held = NOT_HELD;
      this.endChain();
    }
    this.host.body.limit(this.limits);
    this.host.body.slow(moveSpeedOfEffects(this.rules, effects.effects, effects.count));
  }

  /**
   * Toggles the stance (R or the HUD's button, P2.2-T13): drawn to sheathed or back. Sheathing drops a held press, so a
   * click buffered before it never swings after it.
   *
   * @returns True when it toggled; false for a fight with no stance.
   */
  public toggleStance(): boolean {
    const stance = this.host.stance;
    if (stance === undefined) {
      return false;
    }
    if (stance.apply("toggle") === "sheathed") {
      this.held = NOT_HELD;
    }
    return true;
  }

  /**
   * Reads the player's own `vitals`: a change to dead lays the character down and drops a held press, and back to
   * alive stands it up with its weapon sheathed (P2.2-T13) and sounds the standing up (ADR-085); the death's sound is
   * the death screen's `player_death` cue.
   *
   * @param dead Whether the vitals say dead.
   * @param nowMs Scene time in ms.
   */
  public vitals(dead: boolean, nowMs: number): void {
    if (dead) {
      this.held = NOT_HELD;
      this.endChain();
    }
    if (dead !== this.deadYet) {
      if (!dead) {
        this.host.sound?.stood();
      }
      this.deadYet = dead;
      this.host.body.setDead(dead, nowMs);
      if (!dead) {
        this.host.stance?.apply("respawn");
      }
    }
  }

  /**
   * Reads the player's own resources from its `vitals`, which a slot's cost is checked against before its act is sent.
   * The page never spends them: only the next `vitals` moves them. The values are copied, so a decoder's reused record
   * may be handed in.
   *
   * @param vitals Health, Stamina and Focus with their maximums as the last `vitals` carried them.
   */
  public resources(vitals: Readonly<PoolsAndMaximums>): void {
    this.pools.health = vitals.health;
    this.pools.stamina = vitals.stamina;
    this.pools.focus = vitals.focus;
    this.maximums.health = vitals.maxHealth;
    this.maximums.stamina = vitals.maxStamina;
    this.maximums.focus = vitals.maxFocus;
  }

  /**
   * Forgets what of the fight names the map a seamless crossing leaves (ADR-147 decisions 18 and 23): the plan, whose
   * acts may aim at a handle of that map, the kill tallies by handle, and a held press. The fight itself carries over on
   * the server, so the cooldowns, the limits, the stance and the resources stay as they are and the map reached's frames
   * go on from them.
   */
  public crossed(): void {
    this.held = NOT_HELD;
    this.plan.reset();
    this.killers.reset();
    this.chatKillers.reset();
  }

  /**
   * Forgets the session's death, its limits, resources, ready ticks, acknowledgements, held press, its run of unpaid
   * swings and its attackers' names, as a session ends; the next session's character starts standing, free, with every ability ready and its
   * weapon sheathed, since a welcome resets the fight (P2.2-T13).
   */
  public reset(): void {
    this.host.stance?.apply("welcome");
    this.deadYet = false;
    this.held = NOT_HELD;
    this.limits = 0;
    this.weak = false;
    this.resources(UNKNOWN_POOLS);
    this.host.body.limit(0);
    this.readiness.reset();
    this.plan.reset();
    this.killers.reset();
    this.chatKillers.reset();
  }

  /**
   * Ends the page's chain, as a stun or a death ends it on the map server: its later strips and root stop, and presses
   * no longer wait for its root.
   */
  private endChain(): void {
    if (this.readiness.busy) {
      this.host.body.interrupt();
      this.readiness.endBusy();
    }
  }

  /**
   * The name a death line gives a periodic effect by its number: its `periodic.cause`.
   *
   * @param effect The effect's number.
   * @returns The cause, or null for a number the page's rules do not hold or an effect that deals nothing over time.
   */
  private causeOfEffect(effect: number): string | null {
    return effectByNumber(this.rules, effect)?.periodic?.cause ?? null;
  }

  /**
   * The name of an ability by its number.
   *
   * @param ability The number, 0 for none.
   * @returns The name, or null for none or a number the page's rules do not hold.
   */
  private nameOfAbility(ability: number): string | null {
    return ability === 0 ? null : (abilityByNumber(this.rules, ability)?.name ?? null);
  }
}
